/**
 * FAQSection — fetches published FAQ items from Firestore and renders an
 * accessible accordion. Used both on the static HomePage and via the CMS
 * embed shortcode `<div data-gsgo-embed="faq-section"></div>`.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { getPublishedFAQItems, type FAQItem } from '@/lib/cms';

interface FAQSectionProps {
  /** Override items instead of fetching from Firestore (e.g. static homepage). */
  staticItems?: Array<{ q: string; a: string }>;
  /** Section heading. Defaults to "Frequently Asked Questions". */
  heading?: string;
  /** Small eyebrow label above the heading. Defaults to "FAQ". */
  eyebrow?: string;
}

export function FAQSection({
  staticItems,
  heading = 'Frequently Asked Questions',
  eyebrow = 'FAQ',
}: FAQSectionProps): JSX.Element {
  const [items, setItems] = useState<Array<{ q: string; a: string }>>([]);
  const [loading, setLoading] = useState(!staticItems);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (staticItems) {
      setItems(staticItems);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPublishedFAQItems()
      .then((rows: FAQItem[]) => {
        if (!cancelled) {
          setItems(rows.map((r) => ({ q: r.question, a: r.answer })));
        }
      })
      .catch((err: unknown) => {
        console.error('[FAQSection] Failed to load FAQ items from Firestore:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [staticItems]);

  if (loading) {
    return (
      <section className="py-16 sm:py-20 bg-white" aria-label="FAQ loading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse h-14 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section
        className="py-16 sm:py-20 bg-white border border-dashed border-gray-200 rounded-xl"
        aria-labelledby="faq-heading"
        id="faq"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center py-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-600 flex items-center justify-center gap-1.5 mb-2">
            <HelpCircle size={14} aria-hidden="true" />
            {eyebrow}
          </p>
          <h2 id="faq-heading" className="text-xl font-bold text-gray-700 mb-2">
            {heading}
          </h2>
          <p className="text-sm text-gray-500">
            No FAQ items yet. Add and publish items in Admin → FAQ for them to appear here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 sm:py-20 bg-white"
      aria-labelledby="faq-heading"
      id="faq"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-600 flex items-center gap-1.5 mb-2">
            <HelpCircle size={14} aria-hidden="true" />
            {eyebrow}
          </p>
          <h2
            id="faq-heading"
            className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2"
          >
            {heading}
          </h2>
        </div>

        {/* Accordion */}
        <dl className="space-y-3">
          {items.map((item, idx) => {
            const isOpen = openIndex === idx;
            const itemId = `faq-item-${idx}`;
            const panelId = `faq-panel-${idx}`;
            return (
              <div
                key={idx}
                className="border border-gray-200 rounded-xl bg-gray-50/50 overflow-hidden transition-shadow hover:shadow-sm"
              >
                <dt>
                  <button
                    id={itemId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIndex(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm sm:text-base font-medium text-gray-800 hover:text-blue-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
                  >
                    <span>{item.q}</span>
                    <ChevronDown
                      size={18}
                      className={`shrink-0 text-gray-400 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-blue-600' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                </dt>
                <dd
                  id={panelId}
                  role="region"
                  aria-labelledby={itemId}
                  hidden={!isOpen}
                  className={`overflow-hidden transition-all duration-200 ${
                    isOpen ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <p className="px-5 pb-5 text-sm sm:text-base text-gray-600 leading-relaxed">
                    {item.a}
                  </p>
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </section>
  );
}
